import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, Plus, Trash2, Bell } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

interface Appointment {
  id: string;
  appointment_type: string;
  appointment_date: string;
  title: string;
  notes?: string;
  reminder_enabled: boolean;
  reminder_time?: string;
}

interface AppointmentManagerProps {
  journeyType: 'ivf' | 'pregnancy' | 'postpartum' | 'menstrual' | 'perimenopause' | 'menopause';
}

export default function AppointmentManager({ journeyType }: AppointmentManagerProps) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    appointment_type: '',
    appointment_date: '',
    appointment_time: '',
    title: '',
    notes: '',
    reminder_enabled: true,
    reminder_minutes_before: 60
  });

  const appointmentTypes = {
    ivf: ['Injection', 'Scan', 'Blood Test', 'Doctor Appointment', 'Acupuncture', 'Counseling', 'Other'],
    pregnancy: ['Prenatal Checkup', 'Ultrasound', 'Blood Test', 'Hospital Tour', 'Birthing Class', 'Other'],
    postpartum: ['Baby Checkup', 'Pediatrician', 'Vaccination', 'Mom Checkup', 'Lactation Consultant', 'Other'],
    menstrual: ['Gynecologist', 'Ultrasound', 'Blood Test', 'Follow-up', 'Other'],
    perimenopause: ['Gynecologist', 'Blood Test', 'Hormone Check', 'Bone Density Scan', 'Other'],
    menopause: ['Gynecologist', 'Hormone Check', 'Bone Density Scan', 'Wellness Check', 'Other']
  };

  useEffect(() => {
    loadAppointments();
  }, [journeyType]);

  const loadAppointments = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('user_id', user.id)
        .eq('journey_type', journeyType)
        .order('appointment_date', { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please log in to save appointments');
        return;
      }

      const appointmentDateTime = `${formData.appointment_date}T${formData.appointment_time}:00`;
      const reminderTime = formData.reminder_enabled
        ? new Date(new Date(appointmentDateTime).getTime() - formData.reminder_minutes_before * 60000).toISOString()
        : null;

      const { error } = await supabase
        .from('appointments')
        .insert({
          user_id: user.id,
          journey_type: journeyType,
          appointment_type: formData.appointment_type,
          appointment_date: appointmentDateTime,
          title: formData.title,
          notes: formData.notes || null,
          reminder_enabled: formData.reminder_enabled,
          reminder_time: reminderTime
        });

      if (error) throw error;

      toast.success('Appointment added successfully');
      setShowForm(false);
      setFormData({
        appointment_type: '',
        appointment_date: '',
        appointment_time: '',
        title: '',
        notes: '',
        reminder_enabled: true,
        reminder_minutes_before: 60
      });
      loadAppointments();
    } catch (error: any) {
      console.error('Error saving appointment:', error);
      toast.error('Failed to save appointment');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Appointment deleted');
      loadAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Appointments & Reminders
          </CardTitle>
          <Button onClick={() => setShowForm(!showForm)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Appointment
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Appointment Type</Label>
                <Select value={formData.appointment_type} onValueChange={(value) => setFormData(prev => ({ ...prev, appointment_type: value }))}>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes[journeyType].map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Dr. Smith Appointment"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.appointment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointment_date: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.appointment_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointment_time: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any additional details..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  id="reminder"
                  checked={formData.reminder_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, reminder_enabled: checked }))}
                />
                <Label htmlFor="reminder" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Enable Reminder
                </Label>
              </div>

              {formData.reminder_enabled && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="reminder-time" className="text-sm">Remind me:</Label>
                  <Select 
                    value={formData.reminder_minutes_before.toString()} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, reminder_minutes_before: parseInt(value) }))}
                  >
                    <SelectTrigger id="reminder-time" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min before</SelectItem>
                      <SelectItem value="30">30 min before</SelectItem>
                      <SelectItem value="60">1 hour before</SelectItem>
                      <SelectItem value="120">2 hours before</SelectItem>
                      <SelectItem value="1440">1 day before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={!formData.appointment_type || !formData.appointment_date || !formData.appointment_time || !formData.title}>
                Save Appointment
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {appointments.length === 0 && !showForm && (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No appointments scheduled yet</p>
            <p className="text-sm">Add an appointment to get started</p>
          </div>
        )}

        {appointments.length > 0 && (
          <div className="space-y-3">
            {appointments.map(apt => (
              <div key={apt.id} className="flex items-start justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">{apt.title}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {apt.appointment_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(parseISO(apt.appointment_date), 'MMM dd, yyyy')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(parseISO(apt.appointment_date), 'h:mm a')}
                    </div>
                    {apt.reminder_enabled && (
                      <div className="flex items-center gap-1 text-accent">
                        <Bell className="h-3 w-3" />
                        Reminder on
                      </div>
                    )}
                  </div>
                  {apt.notes && (
                    <p className="text-sm text-muted-foreground mt-1">{apt.notes}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(apt.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}